import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MiniPDP from '../../../components/MiniPDP/MiniPDP';
import { useDispatch, useSelector } from 'react-redux';
import { cartActions as storeCartActions } from '../../../reducers/storeCart';
import * as types from '../../../types';
import useSubscriptionProducts from '../../../hooks/useSubscriptionProducts';
import { PromotionBanner } from '@barkinglabs/promotions-react';
import { PromotionConfigurationKind } from '@barkinglabs/promotions-types';
import Loading from '../../../components/Loading';
import { generateID } from '../../../lib/util';
import { applyPartnerCodeToCartWithPrefixRetry, getUrlPartnerCode } from '../../../lib/giftWithPurchasePromo';
import { useCartMode } from '../../../lib/cartModes';
import setCouponCookie from '../../../lib/setCouponCookie';
import setPartnerCookie from '../../../lib/setPartnerCookie';
import { useLocale } from '../../../contexts/LocaleContext';

export const RedeemMini = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const siteConfig = useSelector((state: types.AppState) => state.config.siteConfig);

  const { cartSelector } = useCartMode();
  const cart = useSelector(cartSelector);
  const { currency, country } = useLocale();

  const productId = types.miniCollarId;

  const subscriptionProducts = useSubscriptionProducts(productId);
  const filteredSubscriptionProducts = useMemo(
    () =>
      subscriptionProducts.filter(
        (subscriptionProduct) =>
          subscriptionProduct.renewalMonths === 6 &&
          (siteConfig.miniPlusByDefault ? subscriptionProduct.isPlusPlan : !subscriptionProduct.isPlusPlan),
      ),
    [subscriptionProducts, siteConfig.miniPlusByDefault],
  );

  const [validatingCouponCode, setValidatingCouponCode] = useState(true);
  const [isValidCouponCode, setIsValidCouponCode] = useState(false);

  useEffect(() => {
    const validateCoupon = async () => {
      const urlPartnerCode = getUrlPartnerCode();
      if (!urlPartnerCode) {
        setValidatingCouponCode(false);
        return;
      }

      if (cart.couponCode === urlPartnerCode) {
        setIsValidCouponCode(true);
        setValidatingCouponCode(false);
        return;
      }

      try {
        const cartItemId = generateID();
        const mockCart: types.SubscriptionCart = {
          cartItems: {
            [cartItemId]: {
              forModuleId: productId,
              cartItemId,
              lineItem: {
                lineItemId: generateID(),
                sku: filteredSubscriptionProducts[0].sku,
              },
              quantity: 1,
            },
          },
          currency: currency,
        };

        const { result, codeToApply } = await applyPartnerCodeToCartWithPrefixRetry(mockCart, urlPartnerCode);
        if (
          result.kind === 'success' &&
          result.value.discount.type === 'dollars' &&
          result.value.discount.discountAmountInCents === 9900
        ) {
          setCouponCookie(codeToApply);
          setPartnerCookie(true);
          dispatch(storeCartActions.addCoupon(codeToApply));
          setIsValidCouponCode(true);
        }
      } catch (error) {
        console.error('Error validating coupon:', error);
      } finally {
        setValidatingCouponCode(false);
      }
    };

    validateCoupon();
  }, [cart.couponCode, dispatch, filteredSubscriptionProducts, productId, currency, country]);

  if (validatingCouponCode) {
    return <Loading />;
  }

  return (
    <>
      {
        <PromotionBanner
          className={'promotionBanner'}
          promotion={{
            configuration: {
              kind: PromotionConfigurationKind.SIMPLE,
              title: isValidCouponCode
                ? t('exclusive_offer_amount_off_auto_applied_at_checkout', { amount: 99 })
                : t('exclusive_offer_add_promo_code_at_checkout_for_amount_off', { amount: 99 }),
              extraCss: `.fi-promotion__promoContainer { position: sticky; top: 0; z-index: 9999; width: 100%; background-color: #FFFF01; }`,
              countdown: 'none',
              imageUrl: undefined,
              backgroundImageUrl: undefined,
              subtitle: undefined,
              couponCode: undefined,
            },
          }}
          promotionEnd={new Date()}
        />
      }
      <MiniPDP
        availableSubscriptions={filteredSubscriptionProducts}
        hasValidRedemptionCode={isValidCouponCode}
        redemptionType="gwp_standard"
        preselectedSubscriptionSku={filteredSubscriptionProducts[0]?.sku}
      />
    </>
  );
};
